import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  CodeChip,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  type BadgeTone,
} from '@provenance/design';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Plug, RefreshCw, Search, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { auDateTime, relative } from '../lib/format';
import { useDebounced } from '../lib/useDebounced';

interface IntegratorRow {
  id: string;
  name: string;
  status: string;
  activeKeys: number;
  lastUsedAt: string | null;
  chambers: string[];
}

interface IntegratorDetail {
  id: string;
  name: string;
  status: string;
  contactName: string | null;
  contactEmail: string;
  suspendedReason: string | null;
  keys: Array<{ id: string; keyId: string; label: string; environment: string; last4: string; state: string; expiresAt: string | null; lastUsedAt: string | null; revokedAt: string | null }>;
  grants: Array<{ id: string; chamberName: string; active: boolean; operations: string[] }>;
  webhooks: Array<{ id: string; url: string; events: string[]; active: boolean; deliveryCount: number; alertingSince: string | null }>;
}

interface Requests {
  requests: Array<{ id: string; reference: string; certificateNo: string | null; chamberName: string; status: string; receivedAt: string; outcome: string }>;
  failedCalls: Array<{ id: string; at: string; method: string; path: string; status: number; chamberName: string | null; explained: string; requestId: string }>;
}

interface Delivery {
  id: string;
  eventType: string;
  status: string;
  attempt: number;
  responseStatus: number | null;
  responseBodyExcerpt: string | null;
  errorMessage: string | null;
  createdAt: string;
}

const DELIVERY_TONE: Record<string, BadgeTone> = { SUCCEEDED: 'ok', PENDING: 'info', FAILED: 'warn', DEAD_LETTERED: 'stop' };
const KEY_TONE: Record<string, BadgeTone> = { ACTIVE: 'ok', EXPIRING: 'warn', EXPIRED: 'stop', REVOKED: 'neutral' };

/** Integrators, for Provenir Support (T-148). The Portal Admin's view, read-only. */
export function SupportIntegrators() {
  const list = useQuery({
    queryKey: ['support-integrators'],
    queryFn: () => api.get<IntegratorRow[]>('/api/console/support/integrators'),
  });
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Integrators"
        lede="Systems that lodge certificates through the API, such as SageDocs. Look up a request by their reference, see every notification sent to them, redeliver one, or test the connection."
      />
      {list.isLoading ? (
        <Skeleton className="h-48" />
      ) : (list.data ?? []).length === 0 ? (
        <Card>
          <EmptyState icon={<Plug className="size-5" />} title="No integrators yet" description="A Portal Admin onboards them." />
        </Card>
      ) : (
        <TableShell>
          <Table>
            <Thead>
              <Tr>
                <Th>Integrator</Th>
                <Th>Status</Th>
                <Th>Active keys</Th>
                <Th>Last call</Th>
                <Th>Chambers</Th>
              </Tr>
            </Thead>
            <Tbody>
              {list.data!.map((i) => (
                <Tr key={i.id}>
                  <Td>
                    <Link to={`/support/integrators/${i.id}`} className="font-medium underline-offset-2 hover:underline">
                      {i.name}
                    </Link>
                  </Td>
                  <Td>
                    <Badge tone={i.status === 'ACTIVE' ? 'ok' : 'stop'} size="sm">{i.status.toLowerCase()}</Badge>
                  </Td>
                  <Td className="pv-num">{i.activeKeys}</Td>
                  <Td className="[font-size:12.5px] text-[var(--pv-muted)]">{i.lastUsedAt ? relative(i.lastUsedAt) : 'Never'}</Td>
                  <Td className="[font-size:12.5px]">{i.chambers.join(', ') || '—'}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      )}
    </div>
  );
}

export function SupportIntegratorDetail() {
  const { id } = useParams<{ id: string }>();
  const [reference, setReference] = useState('');
  const term = useDebounced(reference.trim(), 300);
  const [endpoint, setEndpoint] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['support-integrator', id],
    queryFn: () => api.get<IntegratorDetail>(`/api/console/support/integrators/${id}`),
  });
  const requests = useQuery({
    queryKey: ['support-integrator-requests', id, term],
    queryFn: () => api.get<Requests>(`/api/console/support/integrators/${id}/requests`, { reference: term || undefined }),
  });
  useEffect(() => {
    if (!endpoint && detail.data?.webhooks[0]) setEndpoint(detail.data.webhooks[0].id);
  }, [detail.data, endpoint]);

  if (detail.isLoading) return <Skeleton className="h-96" />;
  if (!detail.data) {
    return (
      <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Not found">
        {detail.error instanceof ApiError ? detail.error.message : 'That integrator is not available.'}
      </Callout>
    );
  }
  const d = detail.data;

  return (
    <div className="flex flex-col gap-5">
      <Link to="/support/integrators" className="inline-flex items-center gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)] hover:text-[var(--pv-ink)]">
        <ArrowLeft aria-hidden className="size-3.5" /> All integrators
      </Link>
      <PageHeader
        title={d.name}
        lede={`${d.contactName ?? 'Contact'} · ${d.contactEmail}${d.suspendedReason ? ` · Suspended: ${d.suspendedReason}` : ''}`}
      />

      <Card>
        <CardHeader title="Their requests" description="Search by their reference. The outcome is what the chamber decided, in words." />
        <div className="relative mt-3 max-w-md">
          <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--pv-faint)]" />
          <Input aria-label="Their reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Their reference" className="pl-9" />
        </div>
        {requests.isLoading ? (
          <Skeleton className="mt-3 h-24" />
        ) : (requests.data?.requests ?? []).length === 0 ? (
          <p className="mt-3 [font-size:13px] text-[var(--pv-muted)]">
            {term ? `No request with a reference containing “${term}” has reached Provenir.` : 'No requests yet.'}
          </p>
        ) : (
          <ul className="mt-3 flex list-none flex-col p-0">
            {requests.data!.requests.map((r) => (
              <li key={r.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-[var(--pv-line)] py-2 last:border-b-0">
                <CodeChip>{r.reference}</CodeChip>
                <span className="[font-size:13px]">{r.outcome}</span>
                <span className="ml-auto [font-size:12px] text-[var(--pv-faint)]">
                  {r.chamberName} · received {auDateTime(r.receivedAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {(requests.data?.failedCalls ?? []).length > 0 && (
          <>
            <p className="mt-4 [font-size:var(--pv-text-xs)] font-semibold">Calls refused in the last 14 days</p>
            <ul className="mt-1.5 flex list-none flex-col p-0">
              {requests.data!.failedCalls.map((f) => (
                <li key={f.id} className="flex flex-wrap items-baseline gap-x-3 border-b border-[var(--pv-line)] py-2 last:border-b-0 [font-size:13px]">
                  <Badge tone="stop" size="sm">{f.status}</Badge>
                  <span className="pv-mono [font-size:12px]">{f.method} {f.path}</span>
                  <span>{f.explained}</span>
                  <span className="ml-auto [font-size:12px] text-[var(--pv-faint)]">{auDateTime(f.at)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title="Keys" description="Secrets are never shown. Changing keys is a Portal Admin's job." />
          <ul className="mt-3 flex list-none flex-col p-0">
            {d.keys.map((k) => (
              <li key={k.id} className="flex flex-wrap items-center gap-2 border-b border-[var(--pv-line)] py-2 last:border-b-0 [font-size:13px]">
                <span className="font-medium">{k.label}</span>
                <CodeChip>…{k.last4}</CodeChip>
                <Badge tone={KEY_TONE[k.state] ?? 'neutral'} size="sm">{k.state.toLowerCase()}</Badge>
                <span className="ml-auto [font-size:12px] text-[var(--pv-muted)]">
                  {k.lastUsedAt ? `used ${relative(k.lastUsedAt)}` : 'never used'}
                  {k.expiresAt ? ` · expires ${new Date(k.expiresAt).toLocaleDateString('en-AU')}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <Card>
          <CardHeader title="Chambers granted" />
          <ul className="mt-3 flex list-none flex-col p-0">
            {d.grants.map((g) => (
              <li key={g.id} className="flex items-center gap-2 border-b border-[var(--pv-line)] py-2 last:border-b-0 [font-size:13px]">
                <span className="font-medium">{g.chamberName}</span>
                <Badge tone={g.active ? 'ok' : 'neutral'} size="sm">{g.active ? 'active' : 'revoked'}</Badge>
                <span className="ml-auto [font-size:12px] text-[var(--pv-muted)]">{g.operations.join(', ').toLowerCase()}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card>
        <CardHeader title="Notifications" description="Every notification sent, what their system answered, and a way to send it again." />
        {d.webhooks.length === 0 ? (
          <p className="mt-3 [font-size:13px] text-[var(--pv-muted)]">No notification endpoint configured: they poll for status instead.</p>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap gap-2">
              {d.webhooks.map((w) => (
                <Button key={w.id} size="sm" variant={endpoint === w.id ? 'primary' : 'secondary'} onClick={() => setEndpoint(w.id)}>
                  {hostOf(w.url)}
                  {w.alertingSince && ' · failing'}
                </Button>
              ))}
            </div>
            {endpoint && <Deliveries endpointId={endpoint} />}
          </>
        )}
      </Card>
    </div>
  );
}

function Deliveries({ endpointId }: { endpointId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [testId, setTestId] = useState<string | null>(null);
  const deliveries = useQuery({
    queryKey: ['support-deliveries', endpointId],
    queryFn: () => api.get<Delivery[]>(`/api/console/support/integrators/webhooks/${endpointId}/deliveries`),
  });
  const test = useMutation({
    mutationFn: () => api.post<{ deliveryId: string }>(`/api/console/support/integrators/webhooks/${endpointId}/test`),
    onSuccess: (r) => setTestId(r.deliveryId),
    onError: (error) => toast.error('Could not send the test', error instanceof ApiError ? error.message : ''),
  });
  // Follows the test until it has an answer.
  const testResult = useQuery({
    queryKey: ['support-delivery', testId],
    queryFn: () => api.get<Delivery>(`/api/console/support/integrators/deliveries/${testId}`),
    enabled: Boolean(testId),
    refetchInterval: (q) => (q.state.data && q.state.data.status !== 'PENDING' ? false : 1500),
  });
  const redeliver = useMutation({
    mutationFn: (id: string) => api.post(`/api/console/support/integrators/deliveries/${id}/redeliver`),
    onSuccess: () => {
      toast.success('Sent again', 'The result appears here in a moment.');
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['support-deliveries', endpointId] }), 1500);
    },
    onError: (error) => toast.error('Could not send it again', error instanceof ApiError ? error.message : ''),
  });

  return (
    <div className="mt-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="secondary" leadingIcon={<Send className="size-3.5" />} loading={test.isPending} onClick={() => test.mutate()}>
          Test the connection
        </Button>
        {testResult.data && (
          <span className="flex items-center gap-2 [font-size:13px]" role="status">
            <Badge tone={DELIVERY_TONE[testResult.data.status] ?? 'neutral'} size="sm">{testResult.data.status.toLowerCase().replace('_', ' ')}</Badge>
            {testResult.data.status === 'PENDING'
              ? 'Sending…'
              : testResult.data.responseStatus
                ? `Their system answered HTTP ${testResult.data.responseStatus}`
                : testResult.data.errorMessage ?? ''}
          </span>
        )}
      </div>
      {deliveries.isLoading ? (
        <Skeleton className="h-24" />
      ) : (deliveries.data ?? []).length === 0 ? (
        <p className="[font-size:13px] text-[var(--pv-muted)]">Nothing sent to this endpoint yet.</p>
      ) : (
        <TableShell>
          <Table>
            <Thead>
              <Tr>
                <Th>Sent</Th>
                <Th>Event</Th>
                <Th>Result</Th>
                <Th>Their response</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {deliveries.data!.map((x) => (
                <Tr key={x.id}>
                  <Td className="pv-num [font-size:12.5px]">{auDateTime(x.createdAt)}</Td>
                  <Td className="pv-mono [font-size:12px]">{x.eventType}</Td>
                  <Td>
                    <Badge tone={DELIVERY_TONE[x.status] ?? 'neutral'} size="sm">{x.status.toLowerCase().replace('_', ' ')}</Badge>
                    {x.attempt > 1 && <span className="ml-1.5 [font-size:11.5px] text-[var(--pv-faint)]">{x.attempt} attempts</span>}
                  </Td>
                  <Td className="max-w-[20rem] truncate [font-size:12.5px] text-[var(--pv-muted)]">
                    {[x.responseStatus ? `HTTP ${x.responseStatus}` : null, x.errorMessage, x.responseBodyExcerpt].filter(Boolean).join(' · ') || '—'}
                  </Td>
                  <Td>
                    {(x.status === 'FAILED' || x.status === 'DEAD_LETTERED') && (
                      <Button size="sm" variant="ghost" leadingIcon={<RefreshCw className="size-3.5" />} loading={redeliver.isPending && redeliver.variables === x.id} onClick={() => redeliver.mutate(x.id)}>
                        Redeliver
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      )}
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
